import { useController, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Modal, Button, Input, Select, Toggle, DatePicker } from '@/components/ui'
import type { SelectOption } from '@/components/ui'
import { useCreatePenaltyEntry } from '@/features/penalties/hooks/usePenalties'
import { usePenaltyTypes } from '@/features/penalties/hooks/usePenalties'
import { useCompanyMembers } from '@/features/companies/hooks/useCompanies'
import {
  createPenaltyEntrySchema,
  utcTodayEnd,
  type CreatePenaltyEntryFormData,
} from '@/features/penalties/schemas/penalty.schema'
import { getMemberId, getMemberLabel, getMemberSubline } from '@/features/companies/utils/memberLabel'
import { useOrgCurrency } from '@/hooks/useOrgCurrency'

interface CreatePenaltyModalProps {
  companyId: string
  open: boolean
  onClose: () => void
}

export function CreatePenaltyModal({
  companyId,
  open,
  onClose,
}: CreatePenaltyModalProps): React.ReactElement | null {
  const createMutation = useCreatePenaltyEntry()
  const { data: types = [] } = usePenaltyTypes(open ? companyId : null)
  const { data: membersPage } = useCompanyMembers(open ? companyId : null, { page: 1, limit: 100 })
  const members = membersPage?.data ?? []
  const { currency: orgCurrency, currencyOptions } = useOrgCurrency()

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CreatePenaltyEntryFormData>({
    resolver: zodResolver(createPenaltyEntrySchema),
    defaultValues: {
      useCustomType: false,
      penaltyTypeId: '',
      customTypeName: '',
      userId: '',
      amount: undefined,
      currency: orgCurrency,
      penaltyDate: '',
      reason: '',
    },
  })

  const useCustomType = watch('useCustomType')

  const { field: userIdField } = useController({ name: 'userId', control })
  const { field: penaltyTypeIdField } = useController({ name: 'penaltyTypeId', control })
  const { field: currencyField } = useController({ name: 'currency', control })
  const { field: dateField } = useController({ name: 'penaltyDate', control })

  const memberOptions: SelectOption[] = members.map((m) => ({
    value: getMemberId(m),
    label: getMemberLabel(m),
    description: getMemberSubline(m),
  }))

  const typeOptions: SelectOption[] = [
    { value: '', label: 'Select type…' },
    ...types.map((t) => ({ value: t._id, label: t.name })),
  ]

  const onSubmit = (data: CreatePenaltyEntryFormData) => {
    const body = {
      userId: data.userId,
      amount: data.amount,
      currency: data.currency,
      penaltyDate: data.penaltyDate,
      reason: data.reason || undefined,
      ...(data.useCustomType
        ? { customTypeName: data.customTypeName }
        : { penaltyTypeId: data.penaltyTypeId }),
    }
    createMutation.mutate(
      { companyId, body },
      {
        onSuccess: () => {
          reset()
          onClose()
        },
      },
    )
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  if (!open) return null

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Issue penalty"
      footer={
        <div className="flex gap-2">
          <Button
            type="submit"
            form="create-penalty-form"
            variant="primary"
            loading={createMutation.isPending}
          >
            {createMutation.isPending ? 'Issuing…' : 'Issue penalty'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
        </div>
      }
    >
      <form id="create-penalty-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label
            htmlFor="penalty-userId"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Employee <span className="text-red-500">*</span>
          </label>
          <Select
            id="penalty-userId"
            options={memberOptions}
            value={userIdField.value ?? ''}
            onChange={userIdField.onChange}
            placeholder="Select employee…"
            searchable
          />
          {errors.userId && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.userId.message}</p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Toggle
            checked={useCustomType}
            onChange={(v) => {
              setValue('useCustomType', v)
              setValue('penaltyTypeId', '')
              setValue('customTypeName', '')
            }}
            label="Use custom type name"
          />
        </div>

        {useCustomType ? (
          <Input
            {...register('customTypeName')}
            id="penalty-customTypeName"
            label="Custom type name"
            required
            placeholder="e.g. Equipment misuse"
            error={errors.customTypeName?.message}
          />
        ) : (
          <div>
            <label
              htmlFor="penalty-typeId"
              className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Penalty type <span className="text-red-500">*</span>
            </label>
            <Select
              id="penalty-typeId"
              options={typeOptions}
              value={penaltyTypeIdField.value ?? ''}
              onChange={penaltyTypeIdField.onChange}
            />
            {errors.penaltyTypeId && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                {errors.penaltyTypeId.message}
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Input
            {...register('amount', { valueAsNumber: true })}
            id="penalty-amount"
            label="Amount"
            type="number"
            step="0.01"
            min="0.01"
            required
            placeholder="0.00"
            error={errors.amount?.message}
          />
          <div>
            <label
              htmlFor="penalty-currency"
              className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Currency
            </label>
            <Select
              id="penalty-currency"
              options={currencyOptions}
              value={currencyField.value ?? orgCurrency}
              onChange={currencyField.onChange}
            />
          </div>
        </div>

        <div>
          <DatePicker
            id="penalty-date"
            label="Penalty date"
            value={dateField.value ? new Date(dateField.value) : null}
            onChange={(d) => dateField.onChange(d ? d.toISOString().split('T')[0] : '')}
            maxDate={utcTodayEnd()}
            error={errors.penaltyDate?.message}
          />
        </div>

        <div>
          <label
            htmlFor="penalty-reason"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Reason
          </label>
          <textarea
            {...register('reason')}
            id="penalty-reason"
            rows={3}
            placeholder="Describe the reason for this penalty…"
            className="box-border w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
          />
        </div>
      </form>
    </Modal>
  )
}

CreatePenaltyModal.displayName = 'CreatePenaltyModal'
